import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Assessment as ReportIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import LeaveRequestService, { LEAVE_TYPE_LABELS } from '../../services/leave-request.service';
import EmployeeService from '../../services/employee.service';
import authService from '../../services/auth.service';

const getActionByName = (attrs: any): string => {
  if (attrs?.status === 'approved') {
    const approver = attrs?.approvedBy?.data?.attributes;
    return approver ? `${approver.firstName} ${approver.lastName}` : '-';
  }
  if (attrs?.status === 'rejected') {
    const rejector = attrs?.rejectedBy?.data?.attributes;
    return rejector ? `${rejector.firstName} ${rejector.lastName}` : '-';
  }
  return '-';
};

const getActionDate = (attrs: any): string => {
  if (attrs?.status === 'approved' && attrs?.approvalDate) {
    return new Date(attrs.approvalDate).toLocaleDateString();
  }
  if (attrs?.status === 'rejected' && attrs?.rejectionDate) {
    return new Date(attrs.rejectionDate).toLocaleDateString();
  }
  return '';
};

const LeaveRequestList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableEmployees, setAvailableEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchLeaveRequests();
  }, [statusFilter]);

  useEffect(() => {
    fetchAvailableEmployees();
  }, []);

  const fetchAvailableEmployees = async () => {
    try {
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 100,
        'filters[status][$eq]': 'active',
      });
      setAvailableEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['employee', 'approvedBy', 'rejectedBy'],
        'pagination[pageSize]': 100,
        sort: 'createdAt:desc',
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      const response = await LeaveRequestService.getAll(params);
      setLeaveRequests(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('leave.confirmDelete'))) {
      try {
        await LeaveRequestService.delete(id);
        fetchLeaveRequests();
      } catch (error) {
        console.error('Error deleting leave request:', error);
      }
    }
  };

  const getCurrentUserEmployeeId = async (): Promise<number | null> => {
    try {
      const me = await authService.getMe();
      if (me?.email) {
        const empRes = await EmployeeService.getAll({
          'filters[email][$eq]': me.email,
          'pagination[pageSize]': 1,
        });
        if (empRes.data?.data?.length > 0) {
          return empRes.data.data[0].id;
        }
      }
    } catch (err) {
      console.warn('Could not resolve current user employee:', err);
    }
    if (availableEmployees.length > 0) return availableEmployees[0].id;
    return null;
  };

  const handleApprove = async (id: number) => {
    if (window.confirm(t('leave.confirmApprove'))) {
      try {
        const approvedByEmployeeId = await getCurrentUserEmployeeId();
        await LeaveRequestService.approve(id, approvedByEmployeeId);
        fetchLeaveRequests();
      } catch (error: any) {
        console.error('Error approving leave request:', error);
        const errorMessage = error.response?.data?.error?.message || t('leave.approveFailed');
        alert(`Error: ${errorMessage}`);
      }
    }
  };

  const handleReject = async (id: number) => {
    const reason = prompt(t('leave.enterRejectionReason'));
    if (reason) {
      try {
        const rejectedByEmployeeId = await getCurrentUserEmployeeId();
        await LeaveRequestService.reject(id, reason, rejectedByEmployeeId);
        fetchLeaveRequests();
      } catch (error) {
        console.error('Error rejecting leave request:', error);
      }
    }
  };

  const handleExportExcel = () => {
    const rows = filteredLeaveRequests.map((request) => {
      const attrs = request.attributes;
      const employee = attrs?.employee?.data?.attributes;
      const approver = attrs?.approvedBy?.data?.attributes;
      const rejector = attrs?.rejectedBy?.data?.attributes;
      return {
        Employee: employee ? `${employee.firstName} ${employee.lastName}` : '-',
        'Leave Type': LEAVE_TYPE_LABELS[attrs?.leaveType] || attrs?.leaveType || '-',
        'Start Date': attrs?.startDate || '-',
        'End Date': attrs?.endDate || '-',
        Days: attrs?.totalDays ?? '-',
        Status: attrs?.status || '-',
        'Approved By': approver ? `${approver.firstName} ${approver.lastName}` : '-',
        'Rejected By': rejector ? `${rejector.firstName} ${rejector.lastName}` : '-',
        'Action Date': getActionDate(attrs) || '-',
        Reason: attrs?.reason || '-',
        'Rejection Reason': attrs?.rejectionReason || '-',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leave Requests');
    XLSX.writeFile(wb, `Leave-Requests-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getLeaveTypeColor = (type: string) => {
    switch (type) {
      case 'annual_leave':
      case 'vacation':
        return 'primary';
      case 'sick_leave':
      case 'sick':
        return 'error';
      case 'personal':
      case 'casual_leave_with_pay':
        return 'info';
      case 'maternity':
      case 'paternity':
        return 'secondary';
      case 'unpaid':
      case 'loss_of_pay':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredLeaveRequests = leaveRequests.filter((request) => {
    const employee = request.attributes?.employee?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      employee?.firstName?.toLowerCase().includes(searchLower) ||
      employee?.lastName?.toLowerCase().includes(searchLower) ||
      request.attributes?.leaveType?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('leave.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportExcel}
          >
            {t('leave.exportExcel')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReportIcon />}
            onClick={() => navigate('/leave-requests/report')}
          >
            {t('reports.leaveReport')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/leave-requests/create')}
          >
            {t('leave.newRequest')}
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('common.search')}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('leave.searchPlaceholder')}
          />
          <TextField
            select
            label={t('leave.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">{t('leave.allStatus')}</MenuItem>
            <MenuItem value="pending">{t('leave.pending')}</MenuItem>
            <MenuItem value="approved">{t('leave.approved')}</MenuItem>
            <MenuItem value="rejected">{t('leave.rejected')}</MenuItem>
            <MenuItem value="cancelled">{t('leave.cancelled')}</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('leave.employee')}</TableCell>
              <TableCell>{t('leave.leaveType')}</TableCell>
              <TableCell>{t('leave.startDate')}</TableCell>
              <TableCell>{t('leave.endDate')}</TableCell>
              <TableCell>{t('leave.days')}</TableCell>
              <TableCell>{t('leave.status')}</TableCell>
              <TableCell>{t('leave.actionBy')}</TableCell>
              <TableCell>{t('leave.actionDate')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredLeaveRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {t('leave.noRequests')}
                </TableCell>
              </TableRow>
            ) : (
              filteredLeaveRequests.map((request) => {
                const attrs = request.attributes;
                const employee = attrs?.employee?.data?.attributes;
                const isPending = attrs?.status === 'pending';
                const actionBy = getActionByName(attrs);
                const actionDate = getActionDate(attrs);

                return (
                  <TableRow key={request.id}>
                    <TableCell>
                      {employee
                        ? `${employee.firstName} ${employee.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={LEAVE_TYPE_LABELS[attrs?.leaveType] || attrs?.leaveType || '-'}
                        color={getLeaveTypeColor(attrs?.leaveType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(attrs?.startDate)}</TableCell>
                    <TableCell>{formatDate(attrs?.endDate)}</TableCell>
                    <TableCell>
                      <strong>{attrs?.totalDays || '-'}</strong> {t('leave.daysUnit')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`leave.${attrs?.status || 'pending'}`)}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {actionBy !== '-' ? (
                        <Tooltip
                          title={
                            attrs?.status === 'approved'
                              ? t('leave.approvedByTooltip', { name: actionBy })
                              : t('leave.rejectedByTooltip', { name: actionBy })
                          }
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {actionBy}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{actionDate || '-'}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {isPending && (
                          <>
                            <Tooltip title={t('leave.approve')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(request.id)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('leave.reject')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReject(request.id)}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/leave-requests/${request.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(request.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LeaveRequestList;
